import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import type { PriceImportCommitResult } from './types';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/**
 * Writes the price cells and any approved catalogue creations.
 *
 * Returns 200 with counts even when some chunks failed — earlier chunks really
 * did commit, so the result must be read, not just the status code. Re-issuing
 * a commit resumes: already-committed rows are excluded by construction.
 */
type TData = { data: PriceImportCommitResult };
type TError = AxiosError;
type TVariables = { runId: number };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCommitPriceImportRunApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ runId }: TVariables) => {
    try {
      const { data } = await http.post(`${priceImportsEndpointIdentifier}/${runId}/commit`);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${priceImportsEndpointIdentifier}/commit`],
    retry: false,
    ...options,
  });
}
